<script lang="ts" setup>
import DetailInformation from '@/common/components/molecules/DetailInformation.vue';
import BaseGrid from '@/common/components/molecules/grid/BaseGrid.vue';
import { useInternational } from '@/common/locale';
import {
  ALERT_TYPE_VALUE,
  CONDITION_TYPE_CAPTION_MAP,
  normalizeTargetKindForDetail,
} from '@/alert/utils/define';
import { systemAlertCustomAscFunction } from '@/alert/utils/utils';
import { listViewGridOption } from '@/common/define/grid.define';
import { numberWithComma } from '@/common/utils/commonUtils';
import { getHoldTimeName } from '@/config/views/userAlert/alertRuleWindow.uses';
import { getCheckIntervalItems } from '@/config/utils/alert.define';
import { ALERT_CONTROL_TYPE } from '@/config/views/userAlert/alertRuleDataCommon/alertRuleDataCommon.define';
import { getFilterItemsByGridColumns } from '@/common/components/molecules/filterSearch/filterSearch.utils';
import { USER_ENV_KEYS } from '@/common/define/userEnv.define';
import { useAlertTargetDetail } from '@/alert/components/alertTargetList/alertTargetDetail.use';
import { startCase } from 'lodash-es';
import { computed } from 'vue';
import type { CustomColumn } from '@/common/utils';
import type { NotificationType } from '@/alert/utils/notification.types';
import type { OccurrenceItem, ProcessedMetricInfo } from './eventAlertInformation.types';
import type { SystemAlertDetail } from '@/api-client/alert/model/system-alert-detail';
import OccurrenceItemRenderer from './OccurrenceItemRenderer.vue';
import MetricInfoBlock from './MetricInfoBlock.vue';

const SYSTEM_ALERT_BASE_COLUMNS: CustomColumn[] = [
  {
    caption: 'Target',
    field: 'targetName',
    type: 'string',
    clickable: true,
    searchable: true,
    defaultSearchField: true,
  },
  {
    caption: 'Alias',
    field: 'targetAlias',
    type: 'string',
    searchable: true,
    defaultSearchField: true,
  },
  {
    caption: 'Duration',
    field: 'duration',
    type: 'string',
    searchable: true,
  },
  {
    caption: 'Recovery Check',
    field: 'checkInterval',
    type: 'string',
    searchable: true,
  },
  {
    caption: 'Maximum Occurrences',
    field: 'maximumOccurrences',
    type: 'string',
    searchable: true,
  },
  {
    caption: 'Alert Control Duration',
    field: 'checkHoldTime',
    type: 'string',
    searchable: true,
  },
];

interface Props {
  occurrenceItems: OccurrenceItem[];
  alertRuleType: string;
  ruleCriteria: string;
  metricSubType?: string;
  metricInfos: ProcessedMetricInfo[];
  pathRows: unknown[][];
  pathColumns: CustomColumn[];
  contentItemMinWidth: string;
  systemAlertDetails?: SystemAlertDetail[];
}

interface Emit {
  (e: 'click-status-icon', iconType: NotificationType): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emit>();

const { t } = useInternational();
const { setTypeDetail } = useAlertTargetDetail();

// conditionType(icmp/agentDisconnected/upDown...)은 상태 on/off 조건을 쓰는 시스템 룰에서만 내려오며,
// 룰 단위로 한 종류만 내려오므로 컬럼도 1개만 동적으로 붙인다
const conditionTypeColumn = computed<CustomColumn | null>(() => {
  const conditionType = (props.systemAlertDetails ?? []).find(
    (item) => item.conditionType,
  )?.conditionType;
  if (!conditionType) return null;
  return {
    caption: CONDITION_TYPE_CAPTION_MAP[conditionType] ?? startCase(conditionType),
    // field 를 conditionType 별로 구분해야 filterSearch·컬럼 설정이 룰 간에 섞이지 않는다.
    // (storageKey 는 화면당 1개이므로 field 가 같으면 다른 conditionType 룰에 필터가 되살아남)
    field: `conditionValue_${conditionType}`,
    type: 'string',
    width: 120,
    searchable: true,
  };
});

const systemAlertColumns = computed<CustomColumn[]>(() =>
  conditionTypeColumn.value
    ? [...SYSTEM_ALERT_BASE_COLUMNS, conditionTypeColumn.value]
    : SYSTEM_ALERT_BASE_COLUMNS,
);

const systemAlertGridSearchBarOption = computed(() => ({
  use: true,
  filterSearch: {
    use: true,
    storageKey: USER_ENV_KEYS.FILTERSEARCH_alert_eventAlertDetail_systemAlert,
    filterItems: getFilterItemsByGridColumns(systemAlertColumns.value),
  },
}));

const formatValueWithUnit = (value?: number, unit?: string): string => {
  if (value == null) return '';
  const unitName = getCheckIntervalItems().find((item) => item.value === unit)?.name ?? unit ?? '';
  return `${numberWithComma(value)} ${unitName}`;
};

const formatCheckHoldTime = (holdTime?: string, alertMode?: number): string => {
  if (holdTime == null || alertMode == null) return t('WORD.NOT_SET');
  const modeName =
    alertMode === ALERT_CONTROL_TYPE.ALERT_CLEAR ? t('WORD.ALERT_CLEAR') : t('WORD.ALERT_STOP');
  const timeName = getHoldTimeName(holdTime);
  return `${modeName} (${timeName})`;
};

const formatConditionValue = (conditionValue?: boolean): string => {
  if (conditionValue == null) return '';
  return conditionValue ? t('WORD.USE') : t('WORD.UNUSED');
};

const systemAlertRows = computed(() =>
  (props.systemAlertDetails ?? []).map((item) => [
    item.targetName ?? '',
    item.targetAlias ?? '',
    formatValueWithUnit(item.condition?.duration, item.condition?.durationUnit),
    formatValueWithUnit(item.condition?.interval, item.condition?.intervalUnit),
    item.condition?.maximum === -1 ? '∞' : numberWithComma(item.condition?.maximum ?? '∞'),
    formatCheckHoldTime(item.condition?.holdTime, item.condition?.alertMode),
    ...(conditionTypeColumn.value ? [formatConditionValue(item.conditionValue)] : []),
  ]),
);

const onClickSystemAlertCell = ({ field, row }: { field: string; row: unknown[] }) => {
  if (field !== 'targetName') return;
  const clickedTargetName = row[0] as string;
  const detail = props.systemAlertDetails?.find((item) => item.targetName === clickedTargetName);
  if (!detail?.targetId || !detail?.targetKind) return;
  const category = normalizeTargetKindForDetail(detail.targetKind);
  setTypeDetail({
    name: detail.targetName ?? '',
    category,
    targetId: detail.targetId,
  });
};
</script>

<template>
  <detail-information
    :title="t('WORD.EVENT_OCCURRENCE_MESSAGE')"
    use-expand
    :expand-env-key="USER_ENV_KEYS.TOGGLE_alert_eventAlertDetail_occurrenceMessage_expanded"
    class="event-alert-occurrence-message"
  >
    <template #content>
      <v-stack
        :spacing="12"
        class="event-alert-occurrence-message__content"
      >
        <occurrence-item-renderer
          v-for="(item, idx) in occurrenceItems"
          :key="idx"
          :item="item"
          :index="idx"
          :content-item-min-width="contentItemMinWidth"
          @click-status-icon="(methodType) => emit('click-status-icon', methodType)"
        />
      </v-stack>

      <!-- 메트릭/테이블 지표 정보 영역 -->
      <detail-information
        v-if="
          (alertRuleType === ALERT_TYPE_VALUE.METRIC || alertRuleType === ALERT_TYPE_VALUE.TABLE) &&
          metricInfos.length
        "
        :title="t('WORD.METRIC_STAT_INFORMATION')"
        class="event-alert-occurrence-message__metric-information"
      >
        <template #content>
          <template
            v-for="(metric, mIndex) in metricInfos"
            :key="mIndex"
          >
            <metric-info-block
              :metric="metric"
              :label-width="contentItemMinWidth"
              :rule-criteria="ruleCriteria"
              :metric-sub-type="props.metricSubType"
            />
            <separator
              v-if="mIndex < metricInfos.length - 1"
              class="event-alert-occurrence-message__separator"
            />
          </template>
        </template>
      </detail-information>

      <div
        v-if="pathRows && pathRows.length"
        class="event-alert-path-grid-wrapper"
      >
        <base-grid
          :columns="pathColumns"
          :rows="pathRows"
          class="event-alert-path-grid"
        />
      </div>

      <detail-information
        v-if="
          alertRuleType === ALERT_TYPE_VALUE.SYSTEM &&
          systemAlertDetails &&
          systemAlertDetails.length
        "
        :title="t('WORD.ALERT_CONDITIONS_BY_TARGET')"
        class="event-alert-occurrence-message__system-grid"
      >
        <template #content>
          <base-grid
            :columns="systemAlertColumns"
            :rows="systemAlertRows"
            :option="{
              ...listViewGridOption,
              useSelection: { use: false },
              customAscFunction: systemAlertCustomAscFunction,
            }"
            :search-bar-option="systemAlertGridSearchBarOption"
            :column-env-key="USER_ENV_KEYS.GRIDCOLUMNS_alert_eventAlertDetail_systemAlert"
            :export-excel-option="{ title: t('WORD.ALERT_CONDITIONS_BY_TARGET') }"
            @click-cell="onClickSystemAlertCell"
          />
        </template>
      </detail-information>
    </template>
  </detail-information>
</template>

<style scoped lang="scss">
.event-alert-occurrence-message {
  :deep(.detail-information__content) {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 12px;
  }

  &__separator {
    width: 100%;
    height: 1px;
    border-bottom: 1px dashed var(--color-gray-05-05);
  }

  &__system-grid {
    :deep(.modern-grid__container) {
      height: 300px;
    }
  }
}
</style>
